import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  ClockIcon,
  GlobeAltIcon,
  CheckCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import Map from '../layout/map';

const Contact = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    purpose: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setShowError(false);

    try {
      const response = await fetch('/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (result.success) {
        setShowSuccess(true);
        // Reset form
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          purpose: '',
          message: ''
        });
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      setErrorMessage(error.message || 'Failed to send message. Please try again or contact us directly.');
      setShowError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPinIcon,
      title: 'School Address',
      details: [
        'Aureus Cambridge School',
        'F-78, Block \'B\',',
        'North Nazimabad, Karachi'
      ],
      link: 'https://maps.app.goo.gl/qdcY7L8nphQyqA3Y8'
    },
    {
      icon: PhoneIcon,
      title: 'Phone Numbers',
      details: ['+92 326 1116777', '+92 21 36607387'],
      link: 'tel:+923261116777'
    },
    {
      icon: ClockIcon,
      title: 'Office Hours',
      details: [
        'Monday–Saturday',
        '8:00 AM to 4:00 PM'
      ],
      link: null
    },
    {
      icon: EnvelopeIcon,
      title: 'Email',
      details: ['admissions@aureusschool.com'],
      link: 'mailto:admissions@aureusschool.com'
    }
  ];

  const socialMedia = [
    {
      platform: 'Instagram',
      url: 'https://www.instagram.com/aureuscambridgeschool/',
      icon: (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
        </svg>
      )
    },
    {
      platform: 'Facebook',
      url: 'https://www.facebook.com/profile.php?id=61577642811503',
      icon: (
        <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative flex items-center justify-center py-20 overflow-hidden h-[80vh]">
        <div
          className="absolute inset-0 transform bg-center bg-no-repeat bg-cover"
          style={{
            backgroundImage: `url('${require('../../assets/update/Contacts Us Hero Image.jpg')}')`
          }}
        />
        <div className="absolute inset-0 bg-black/60" />

        <div className="relative z-10 max-w-4xl px-4 mx-auto text-center text-white">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mb-2 text-base font-semibold tracking-widest text-white uppercase md:text-lg"
            style={{ letterSpacing: '0.18em' }}
          >
            Get In Touch
          </motion.p>
          <motion.h1
            className="mb-6 text-5xl font-bold md:text-7xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            CONTACT <span className="text-yellow-400">US</span>
          </motion.h1>
          <motion.p
            className="mb-8 text-xl text-gray-200 md:text-2xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            We're here to answer your questions and welcome you to our community
          </motion.p>
        </div>
      </section>

      {/* Contact Information Grid */}
      <section className="py-20 bg-white">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mb-16 text-center"
          >
            <h2 className="mb-4 text-3xl font-extrabold text-blue-900 md:text-4xl" style={{ fontFamily: 'Playfair Display, serif' }}>
              Contact Information
            </h2>
            <p className="text-lg text-slate-700">
              Get in touch with us through any of these channels
            </p>
          </motion.div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="p-6 text-center transition-all duration-300 bg-white border shadow-lg rounded-2xl hover:shadow-xl border-amber-100 flex flex-col justify-between min-h-[280px]"
              >
                <div className="flex items-center justify-center flex-shrink-0 w-16 h-16 mx-auto mb-4 bg-yellow-400 rounded-full">
                  <info.icon className="w-8 h-8 text-black" />
                </div>
                <div className="flex-grow">
                  <h3 className="mb-3 text-lg font-bold break-words text-slate-900">{info.title}</h3>
                  <div className="mb-4 space-y-1">
                    {info.details.map((detail, idx) => (
                      <p key={idx} className="text-sm break-words text-slate-600">{detail}</p>
                    ))}
                  </div>
                </div>
                {info.link && (
                  <div className="mt-auto">
                    <a
                      href={info.link}
                      target={info.link.startsWith('http') ? '_blank' : '_self'}
                      rel={info.link.startsWith('http') ? 'noopener noreferrer' : ''}
                      className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-black no-underline transition-colors duration-200 bg-yellow-400 rounded-full hover:bg-yellow-500"
                    >
                      {info.title === 'School Address' ? 'Get Directions' :
                        info.title === 'Phone Numbers' ? 'Call Now' :
                          info.title === 'Email' ? 'Send Email' : 'Contact'}
                    </a>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Google Map and Contact Form */}
      <section className="py-20 bg-gray-50">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2">

            {/* Google Map */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="mb-6 text-3xl font-extrabold text-blue-900" style={{ fontFamily: 'Playfair Display, serif' }}>Find Us</h2>
              <Map />
              <div className="mt-4 text-center">
                <a
                  href="https://maps.app.goo.gl/qdcY7L8nphQyqA3Y8"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-6 py-3 font-semibold text-black no-underline transition-all duration-300 bg-yellow-400 rounded-full hover:bg-yellow-500 hover:scale-105"
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Get Directions
                </a>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="mb-6 text-3xl font-extrabold text-blue-900" style={{ fontFamily: 'Playfair Display, serif' }}>Send us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block mb-2 text-sm font-medium text-slate-700">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 transition-colors border rounded-lg border-slate-300 focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    placeholder="Enter your full name"
                  />
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium text-slate-700">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 transition-colors border rounded-lg border-slate-300 focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    placeholder="Enter your email address"
                  />
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium text-slate-700">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 transition-colors border rounded-lg border-slate-300 focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    placeholder="Enter your phone number"
                  />
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium text-slate-700">
                    Purpose of Contact *
                  </label>
                  <select
                    name="purpose"
                    value={formData.purpose}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 transition-colors border rounded-lg border-slate-300 focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  >
                    <option value="">Select purpose</option>
                    <option value="admissions">Admissions</option>
                    <option value="feedback">Feedback</option>
                    <option value="careers">Careers</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block mb-2 text-sm font-medium text-slate-700">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    className="w-full px-4 py-3 transition-colors border rounded-lg border-slate-300 focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    placeholder="Enter your message"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-6 py-3 font-semibold transition-colors duration-200 bg-yellow-400 rounded-full hover:bg-yellow-500 disabled:bg-yellow-300 disabled:cursor-not-allowed text-slate-900"
                >
                  {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <div className="w-5 h-5 mr-2 border-b-2 rounded-full animate-spin border-slate-900"></div>
                      Sending...
                    </div>
                  ) : (
                    'Send Message'
                  )}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Social Media Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl px-4 mx-auto text-center sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <GlobeAltIcon className="w-12 h-12 mx-auto mb-4 text-yellow-400" />
            <h2 className="mb-6 text-3xl font-extrabold text-blue-900" style={{ fontFamily: 'Playfair Display, serif' }}>
              Follow Us on Social Media
            </h2>
            <p className="mb-8 text-lg text-slate-700">
              Stay connected with our school community and get the latest updates
            </p>

            <div className="flex flex-col items-center justify-center w-full gap-4 mx-auto sm:flex-row sm:max-w-lg">
              {socialMedia.map((social, index) => (
                <a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center w-full px-6 py-3 font-semibold text-center text-black no-underline transition-all duration-300 bg-yellow-400 rounded-full sm:w-auto hover:bg-yellow-500 hover:scale-105"
                >
                  <span className="flex items-center mr-2">{social.icon}</span>
                  {social.platform}
                </a>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Success Popup */}
      <AnimatePresence>
        {showSuccess && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 50 }}
              transition={{ type: "spring", damping: 25, stiffness: 500 }}
              className="w-full max-w-md p-8 bg-white border border-gray-100 shadow-2xl rounded-3xl"
            >
              <div className="text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", damping: 10, stiffness: 200 }}
                  className="flex items-center justify-center w-20 h-20 mx-auto mb-6 bg-green-500 rounded-full shadow-lg"
                >
                  <CheckCircleIcon className="w-10 h-10 text-white" />
                </motion.div>

                <motion.h3
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="mb-3 text-2xl font-bold text-slate-900"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  Message Sent Successfully!
                </motion.h3>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="mb-6 leading-relaxed text-slate-600"
                >
                  Thank you for reaching out to <strong>Aureus Cambridge School</strong>. We have received your message and will get back to you within 24 hours.
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="p-4 mb-6 border-l-4 border-yellow-400 rounded-lg bg-yellow-50"
                >
                  <p className="text-sm text-yellow-800">
                    📧 We have received your message and will get back to you shortly.
                  </p>
                </motion.div>

                <motion.button
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowSuccess(false)}
                  className="w-full px-8 py-3 font-semibold text-black transition-all duration-300 bg-yellow-400 rounded-full shadow-lg hover:bg-yellow-500 focus:outline-none focus:ring-4 focus:ring-yellow-400 focus:ring-offset-2 hover:shadow-xl"
                >
                  Continue
                </motion.button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Error Popup */}
      <AnimatePresence>
        {showError && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.8, y: 50 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 50 }}
              transition={{ type: "spring", damping: 25, stiffness: 500 }}
              className="w-full max-w-md p-8 bg-white border border-gray-100 shadow-2xl rounded-3xl"
            >
              <div className="text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", damping: 10, stiffness: 200 }}
                  className="flex items-center justify-center w-20 h-20 mx-auto mb-6 rounded-full shadow-lg bg-gradient-to-r from-red-400 to-red-600"
                >
                  <XMarkIcon className="w-10 h-10 text-white" />
                </motion.div>

                <motion.h3
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="mb-3 text-2xl font-bold text-slate-900"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  Message Failed to Send
                </motion.h3>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="mb-6 leading-relaxed text-slate-600"
                >
                  {errorMessage}
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="p-4 mb-6 border-l-4 border-blue-400 rounded-lg bg-gradient-to-r from-blue-50 to-slate-50"
                >
                  <p className="mb-2 text-sm text-blue-800">
                    <strong>Alternative contact methods:</strong>
                  </p>
                  <p className="text-sm text-blue-700">📞 +92 326 1116777</p>
                  <p className="text-sm text-blue-700">📞 +92 21 36607387</p>
                  <p className="text-sm text-blue-700">✉️ admissions@aureusschool.com</p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="flex gap-3"
                >
                  <button
                    onClick={() => setShowError(false)}
                    className="flex-1 px-6 py-3 font-semibold transition-all duration-300 rounded-full text-slate-700 bg-slate-200 hover:bg-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => setShowError(false)}
                    className="flex-1 px-6 py-3 font-semibold text-black transition-all duration-300 rounded-full shadow-lg bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:ring-offset-2 hover:shadow-xl"
                  >
                    Try Again
                  </button>
                </motion.div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Contact; 